import React, { useEffect, useState } from 'react';

import styled from 'styled-components';

import { SkeletonLine } from '~/techstyle-shared/react-components';

import useFadeOutUnmount from '../../../hooks/useFadeOutUnmount';
import { isValidVideoSrc } from '../../../utils/video';

import FilterImage from './FilterImage';
import FilterVideo from './FilterVideo';

const AssetContainer = styled.div`
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
  border-radius: 8px;
`;

const StyledSkeletonLine = styled(SkeletonLine)<{ $isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: opacity 0.3s;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
`;

const MediaWrapper = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  inset: 0;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  transition: opacity 0.3s;
`;

type FilterAssetProps = {
  alt?: string;
  imageSrc?: string;
  isFetchingAssets?: boolean;
  isPlaying?: boolean;
  syncEpochMs?: number;
};

const FilterAsset = ({
  imageSrc,
  alt,
  isFetchingAssets = true,
  isPlaying = true,
  syncEpochMs,
}: FilterAssetProps) => {
  const [isMediaVisible, setIsMediaVisible] = useState(false);
  const [hasVideoError, setHasVideoError] = useState(false);
  const hasValidVideoSrc = isValidVideoSrc(imageSrc);
  const shouldRenderVideo = hasValidVideoSrc && !hasVideoError;
  const { isMounted: showSkeleton, onTransitionEnd: onSkeletonTransitionEnd } =
    useFadeOutUnmount(isFetchingAssets);

  useEffect(() => {
    setHasVideoError(false);
  }, [imageSrc, isFetchingAssets]);

  useEffect(() => {
    if (isFetchingAssets) {
      setIsMediaVisible(false);
      return;
    }

    const rafId = window.requestAnimationFrame(() => {
      setIsMediaVisible(true);
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [isFetchingAssets]);

  return (
    <AssetContainer aria-busy={isFetchingAssets}>
      {showSkeleton && (
        <StyledSkeletonLine
          $isVisible={isFetchingAssets}
          onTransitionEnd={onSkeletonTransitionEnd}
        />
      )}
      <MediaWrapper $isVisible={isMediaVisible}>
        {shouldRenderVideo ? (
          <FilterVideo
            videoSrc={imageSrc}
            alt={alt}
            isFetchingAssets={isFetchingAssets}
            isPlaying={isPlaying}
            onVideoError={() => setHasVideoError(true)}
            syncEpochMs={syncEpochMs}
          />
        ) : (
          <FilterImage
            src={imageSrc}
            alt={alt}
            isFetchingAssets={isFetchingAssets}
          />
        )}
      </MediaWrapper>
    </AssetContainer>
  );
};

export default FilterAsset;
